import { z } from "zod";
import { IsoDateTime, OperatorEnvironment, Product } from "../common.ts";
import { CheckFlagV1, JurisdictionCode } from "../ports.ts";
import { PageParams } from "./common.ts";

// Platform administration routes of the Explorer API (`/explorer/v1/platform/*`, design D20, ADR
// 0008, spec platform-administration). Served by the `operator-auth` platform interface
// (operators, environments, products, origins, credentials) and by `iam` (organizations, operator
// links, invitations). Every route needs a live console session and a platform permission code
// (`platform.operators.manage` or `platform.credentials.manage`); anyone else gets `404`.
//
// Request schemas check the shape (types, unknown keys rejected); the value rules (ranges, slug
// and origin syntax, known jurisdictions, the 1,800 s revalidation cap) are the domain's, so a
// refused value is always answered `400 invalid_request` naming the fields (Explorer API error
// catalog, `EXPLORER_API_ERRORS`; a refused revalidation window adds the reason
// `invalid_revalidation_window`).

/** Reason of a refused revalidation window (`valid_for_seconds` or `next_check_in_seconds` > 1,800). */
export const INVALID_REVALIDATION_WINDOW_REASON = "invalid_revalidation_window";

/**
 * Path parameters. No route takes an environment (ADR 0011, design D6 of
 * bind-operator-environment-to-installation): an operator has exactly one environment in an
 * installation, the installation's own.
 */
export const PlatformOperatorParamsV1 = z.object({ id: z.uuid() }).strict();
export const PlatformProductParamsV1 = z.object({ id: z.uuid(), product: Product }).strict();
export const PlatformCredentialParamsV1 = z.object({ id: z.uuid() }).strict();

// ---- operators, environments, products ---------------------------------------------------------

/**
 * `POST /platform/organizations/{id}/operators`: the operator is created inside that organization
 * and gets exactly one environment, the installation's (`staging` or `production`). The same
 * operator is created separately in each installation, with the same slug and an `operator_id` of
 * its own per installation.
 */
export const CreateOperatorRequestV1 = z
  .object({
    /** Lower-case kebab-case, 1-63 characters, unique (`409 conflict` when taken). */
    slug: z.string().min(1).max(63),
    /** Company (legal) name, 1-200 printable characters. */
    name: z.string().min(1).max(200),
  })
  .strict()
  .meta({ id: "CreateOperatorRequestV1" });
export type CreateOperatorRequestV1 = z.infer<typeof CreateOperatorRequestV1>;

export const RevalidationWindowV1 = z
  .object({
    valid_for_seconds: z.int().positive(),
    next_check_in_seconds: z.int().positive(),
    buffer_seconds: z.int().min(0),
  })
  .strict()
  .meta({ id: "RevalidationWindowV1" });
export type RevalidationWindowV1 = z.infer<typeof RevalidationWindowV1>;

export const PlatformProductConfigV1 = z
  .object({ product: Product, enabled: z.boolean(), revalidation: RevalidationWindowV1 })
  .strict()
  .meta({ id: "PlatformProductConfigV1" });

/** The `EnvironmentPolicy` of an operator environment as the Platform area edits it. */
export const PlatformEnvironmentPolicyV1 = z
  .object({
    precise_location_days: z.int().positive(),
    raw_device_network_days: z.int().positive(),
    decision_retention_years: z.int().positive(),
    allowed_jurisdictions: z.array(JurisdictionCode),
    failure_rate_highlight: z.number(),
    shared_device_player_threshold: z.int(),
    max_travel_speed_kmh: z.int(),
    /** Check flags enabled for the environment; a disabled required check denies (task 4.8). */
    enabled_checks: z.array(CheckFlagV1),
  })
  .strict()
  .meta({ id: "PlatformEnvironmentPolicyV1" });
export type PlatformEnvironmentPolicyV1 = z.infer<typeof PlatformEnvironmentPolicyV1>;

export const PlatformEnvironmentV1 = z
  .object({
    environment: OperatorEnvironment,
    issuer: z.string().min(1),
    allowed_origins: z.array(z.string()),
    session_token_ttl_seconds: z.int().positive(),
    rate_limit: z.object({ per_second: z.int().positive(), burst: z.int().positive() }).strict(),
    policy: PlatformEnvironmentPolicyV1,
    products: z.array(PlatformProductConfigV1),
  })
  .strict()
  .meta({ id: "PlatformEnvironmentV1" });
export type PlatformEnvironmentV1 = z.infer<typeof PlatformEnvironmentV1>;

/**
 * Status of an operator: `deactivated` is terminal (change add-console-account-settings, design D9):
 * credentials revoked, configuration read-only, console access removed, slug kept reserved.
 * `suspended` is reserved for a future reversible suspension (no command sets it).
 */
export const PlatformOperatorStatusV1 = z
  .enum(["active", "suspended", "deactivated"])
  .meta({ id: "PlatformOperatorStatusV1" });
export type PlatformOperatorStatusV1 = z.infer<typeof PlatformOperatorStatusV1>;

export const PlatformOperatorV1 = z
  .object({
    operator_id: z.uuid(),
    slug: z.string(),
    name: z.string(),
    status: PlatformOperatorStatusV1,
    created_at: IsoDateTime,
    /** When, by whom (console user id) and why it was deactivated; `null` while not deactivated. */
    deactivated_at: IsoDateTime.nullable(),
    deactivated_by: z.uuid().nullable(),
    deactivation_reason: z.string().nullable(),
    /**
     * Deactivation date plus the operator's `decision_retention_years` (of its environment): the
     * earliest date of the final purge of its tier-3 data (owner decision O-5,
     * the purge job itself is a later change). `null` while not deactivated.
     */
    purge_after: IsoDateTime.nullable(),
    /**
     * Whether `POST /platform/operators/{id}/deactivate` is available for this operator: the
     * operator is active and the installation's release flag `operator-auth.operator-deactivation`
     * is on. The Platform area shows its "Danger zone" only when true.
     */
    can_deactivate: z.boolean(),
    /** The operator's single environment in this installation (the installation's own). */
    environment: PlatformEnvironmentV1,
  })
  .strict()
  .meta({ id: "PlatformOperatorV1" });
export type PlatformOperatorV1 = z.infer<typeof PlatformOperatorV1>;

/** Longest deactivation reason (characters after trimming). */
export const OPERATOR_DEACTIVATION_REASON_MAX = 500;

/**
 * `POST /platform/operators/{id}/deactivate` (`platform.operators.manage`): terminal. The reason
 * (1-500 characters after trimming) is audited and shown; `confirm_slug` must be the operator's
 * slug, typed by the Platform Administrator (`400 invalid_request` naming `confirm_slug`
 * otherwise, nothing changes). A non-active operator answers `409 operator_deactivated`.
 */
export const OperatorDeactivationRequestV1 = z
  .object({
    reason: z.string().min(1).max(OPERATOR_DEACTIVATION_REASON_MAX),
    confirm_slug: z.string().min(1).max(63),
  })
  .strict()
  .meta({ id: "OperatorDeactivationRequestV1" });
export type OperatorDeactivationRequestV1 = z.infer<typeof OperatorDeactivationRequestV1>;

export const PlatformOperatorListResponseV1 = z
  .object({ operators: z.array(PlatformOperatorV1) })
  .strict()
  .meta({ id: "PlatformOperatorListResponseV1" });
export type PlatformOperatorListResponseV1 = z.infer<typeof PlatformOperatorListResponseV1>;

const MAX_ORIGINS_PER_REQUEST = 20;
const Origin = z.string().min(1).max(253);

/**
 * `PATCH /platform/operators/{id}/environment`: every field optional, at least one;
 * unspecified fields keep their value; all changes apply together or none (400 names the fields).
 */
export const ConfigureEnvironmentRequestV1 = z
  .object({
    session_token_ttl_seconds: z.int().optional(),
    rate_limit: z.object({ per_second: z.int(), burst: z.int().optional() }).strict().optional(),
    policy: z
      .object({
        precise_location_days: z.int(),
        raw_device_network_days: z.int(),
        decision_retention_years: z.int(),
        allowed_jurisdictions: z.array(z.string().max(16)).max(100),
        failure_rate_highlight: z.number(),
        shared_device_player_threshold: z.int(),
        max_travel_speed_kmh: z.int(),
        enabled_checks: z.array(CheckFlagV1).max(CheckFlagV1.options.length),
      })
      .partial()
      .strict()
      .optional(),
    add_origins: z.array(Origin).max(MAX_ORIGINS_PER_REQUEST).optional(),
    remove_origins: z.array(Origin).max(MAX_ORIGINS_PER_REQUEST).optional(),
  })
  .strict()
  .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: "at least one field is required",
  })
  .meta({ id: "ConfigureEnvironmentRequestV1" });
export type ConfigureEnvironmentRequestV1 = z.infer<typeof ConfigureEnvironmentRequestV1>;

/**
 * `PUT /platform/operators/{id}/environment/products/{product}`: enables or disables the
 * product; `revalidation` (enabled only) sets its window, whose `valid_for_seconds` and
 * `next_check_in_seconds` must not exceed 1,800 (reason `invalid_revalidation_window`; the
 * previous window remains).
 */
export const PutProductRequestV1 = z
  .object({ enabled: z.boolean(), revalidation: RevalidationWindowV1.optional() })
  .strict()
  .meta({ id: "PutProductRequestV1" });
export type PutProductRequestV1 = z.infer<typeof PutProductRequestV1>;

/** `PATCH …/environment` and `PUT …/environment/products/{product}` answer the environment. */
export const PlatformEnvironmentResponseV1 = PlatformEnvironmentV1;
export type PlatformEnvironmentResponseV1 = PlatformEnvironmentV1;

// ---- API credentials (`platform.credentials.manage`) --------------------------------------------

/** A credential as listed: the secret only as its masked suffix (`****abcd`). */
export const PlatformCredentialV1 = z
  .object({
    credential_id: z.uuid(),
    client_id: z.string().min(1),
    operator_id: z.uuid(),
    environment: OperatorEnvironment,
    status: z.enum(["active", "revoked"]),
    masked_secret: z.string().regex(/^\*{4}[A-Za-z0-9_-]{1,8}$/),
    created_at: IsoDateTime,
    revoked_at: IsoDateTime.nullable(),
  })
  .strict()
  .meta({ id: "PlatformCredentialV1" });
export type PlatformCredentialV1 = z.infer<typeof PlatformCredentialV1>;

export const PlatformCredentialListResponseV1 = z
  .object({ credentials: z.array(PlatformCredentialV1) })
  .strict()
  .meta({ id: "PlatformCredentialListResponseV1" });
export type PlatformCredentialListResponseV1 = z.infer<typeof PlatformCredentialListResponseV1>;

/**
 * `POST /platform/operators/{id}/credentials`: no fields (the operator comes from the path, the
 * environment is the installation's).
 */
export const CreateCredentialRequestV1 = z
  .object({})
  .strict()
  .meta({ id: "CreateCredentialRequestV1" });

/**
 * The only response that ever carries the `client_secret`: shown once, never stored in plain text,
 * never sent by email or any platform channel. The Platform Administrator hands it to the operator
 * out of band (for example a shared password vault).
 */
export const CreatedCredentialResponseV1 = z
  .object({
    ...PlatformCredentialV1.shape,
    client_secret: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  })
  .strict()
  .meta({ id: "CreatedCredentialResponseV1" });
export type CreatedCredentialResponseV1 = z.infer<typeof CreatedCredentialResponseV1>;

/** `POST /platform/credentials/{id}/revoke`: the reason is audited. */
export const RevokeCredentialRequestV1 = z
  .object({ reason: z.string().trim().min(1).max(200) })
  .strict()
  .meta({ id: "RevokeCredentialRequestV1" });
export type RevokeCredentialRequestV1 = z.infer<typeof RevokeCredentialRequestV1>;

// ---- organizations, operator links, invitations (`iam`) -----------------------------------------

/** `POST /platform/organizations`. */
export const CreateOrganizationRequestV1 = z
  .object({ name: z.string().trim().min(1).max(200) })
  .strict()
  .meta({ id: "CreateOrganizationRequestV1" });
export type CreateOrganizationRequestV1 = z.infer<typeof CreateOrganizationRequestV1>;

export const OrganizationCreatedResponseV1 = z
  .object({ organization_id: z.uuid() })
  .strict()
  .meta({ id: "OrganizationCreatedResponseV1" });
export type OrganizationCreatedResponseV1 = z.infer<typeof OrganizationCreatedResponseV1>;

/** `POST /platform/organizations/{id}/operators`: the operator created inside the organization. */
export const OperatorLinkedResponseV1 = z
  .object({ organization_id: z.uuid(), operator_id: z.uuid() })
  .strict()
  .meta({ id: "OperatorLinkedResponseV1" });
export type OperatorLinkedResponseV1 = z.infer<typeof OperatorLinkedResponseV1>;

/**
 * `POST /platform/organizations/{id}/invitations`: the first Administrator of an active operator of
 * that organization (role Administrator). Refused with `409 conflict` and the reason
 * `operator_not_linked` when the operator does not belong to the organization, and with
 * `409 operator_deactivated` for a deactivated operator. The response is
 * `InvitationIssuedResponseV1` (single-use link token, 72 h, shown once, no password).
 */
export const InviteFirstAdministratorRequestV1 = z
  .object({ operator_id: z.uuid(), email: z.email().max(254) })
  .strict()
  .meta({ id: "InviteFirstAdministratorRequestV1" });
export type InviteFirstAdministratorRequestV1 = z.infer<typeof InviteFirstAdministratorRequestV1>;

/** `POST /platform/administrators/invitations`: platform-level, single use, 72 h, no organization. */
export const InvitePlatformAdministratorRequestV1 = z
  .object({ email: z.email().max(254) })
  .strict()
  .meta({ id: "InvitePlatformAdministratorRequestV1" });
export type InvitePlatformAdministratorRequestV1 = z.infer<
  typeof InvitePlatformAdministratorRequestV1
>;

/** Reason of the `409 conflict` answered to an invitation before the operator is linked. */
export const OPERATOR_NOT_LINKED_REASON = "operator_not_linked";

/** Reason of the `409 conflict` answered to an operator created with a slug already in use. */
export const OPERATOR_SLUG_TAKEN_REASON = "operator_slug_taken";

// ---- overview: organizations, operator organization, pending invitations (`iam`, task 20.9) ------

/** An organization with the operators linked to it (in link order). */
export const PlatformOrganizationV1 = z
  .object({
    organization_id: z.uuid(),
    name: z.string().min(1),
    created_at: IsoDateTime,
    operator_ids: z.array(z.uuid()),
  })
  .strict()
  .meta({ id: "PlatformOrganizationV1" });
export type PlatformOrganizationV1 = z.infer<typeof PlatformOrganizationV1>;

/** `GET /platform/organizations`: page-based (20 by default, at most 100), ordered by name. */
export const PlatformOrganizationListQueryV1 = z
  .object({ ...PageParams })
  .strict()
  .meta({ id: "PlatformOrganizationListQueryV1" });
export type PlatformOrganizationListQueryV1 = z.infer<typeof PlatformOrganizationListQueryV1>;

export const PlatformOrganizationListResponseV1 = z
  .object({
    total: z.int().nonnegative(),
    page: z.int().min(1),
    size: z.int().min(1),
    items: z.array(PlatformOrganizationV1),
  })
  .strict()
  .meta({ id: "PlatformOrganizationListResponseV1" });
export type PlatformOrganizationListResponseV1 = z.infer<typeof PlatformOrganizationListResponseV1>;

/**
 * `GET /platform/operators/{id}/organization`: the organization the operator belongs to (also
 * after it is deactivated), or `null` for an operator created before every operator had one and
 * not adopted yet.
 */
export const OperatorOrganizationResponseV1 = z
  .object({ operator_id: z.uuid(), organization: PlatformOrganizationV1.nullable() })
  .strict()
  .meta({ id: "OperatorOrganizationResponseV1" });
export type OperatorOrganizationResponseV1 = z.infer<typeof OperatorOrganizationResponseV1>;

// ---- organization access overview (`iam`, change reorganize-platform-area-by-organization D16) ---

/** Path of `GET /platform/organizations/{id}/access`. */
export const PlatformOrganizationParamsV1 = z
  .object({ id: z.uuid() })
  .strict()
  .meta({ id: "PlatformOrganizationParamsV1" });
export type PlatformOrganizationParamsV1 = z.infer<typeof PlatformOrganizationParamsV1>;

/**
 * State of the organization's first Administrator: `invited` while the invitation is pending,
 * `active` when accepted and the console user who accepted it is still active, `deactivated` when
 * that console user was deactivated, `expired` or `revoked` before acceptance.
 */
export const FirstAdministratorStateV1 = z.enum([
  "invited",
  "active",
  "deactivated",
  "expired",
  "revoked",
]);
export type FirstAdministratorStateV1 = z.infer<typeof FirstAdministratorStateV1>;

/** The recipient of the organization's latest first-Administrator invitation (no link or token). */
export const PlatformFirstAdministratorV1 = z
  .object({
    email: z.email(),
    /** The operator the invitation was issued for. */
    operator_id: z.uuid().nullable(),
    state: FirstAdministratorStateV1,
    invited_at: IsoDateTime,
    expires_at: IsoDateTime,
    accepted_at: IsoDateTime.nullable(),
  })
  .strict()
  .meta({ id: "PlatformFirstAdministratorV1" });
export type PlatformFirstAdministratorV1 = z.infer<typeof PlatformFirstAdministratorV1>;

/**
 * State of the organization's identity provider: `none`, `configured` (registered but not active)
 * or `active`, with its kind (`null` while there is none). Never its metadata URL, issuer or client
 * id.
 */
export const PlatformOrganizationIdentityProviderV1 = z
  .object({
    state: z.enum(["none", "configured", "active"]),
    kind: z.enum(["saml", "oidc"]).nullable(),
  })
  .strict()
  .meta({ id: "PlatformOrganizationIdentityProviderV1" });
export type PlatformOrganizationIdentityProviderV1 = z.infer<
  typeof PlatformOrganizationIdentityProviderV1
>;

/**
 * `GET /platform/organizations/{id}/access`: the access overview of one organization, read-only.
 * The first Administrator (`null` while no first-Administrator invitation was issued from the
 * Platform area), the number of active console users of the organization and of those who are
 * Administrators of at least one of its active operators, and the identity provider's state. It
 * NEVER carries another console user's email or name, an invitation link, token or digest, or an
 * identity provider secret, metadata, issuer or client id.
 */
export const PlatformOrganizationAccessV1 = z
  .object({
    organization_id: z.uuid(),
    first_administrator: PlatformFirstAdministratorV1.nullable(),
    active_console_users: z.int().nonnegative(),
    active_administrators: z.int().nonnegative(),
    identity_provider: PlatformOrganizationIdentityProviderV1,
  })
  .strict()
  .meta({ id: "PlatformOrganizationAccessV1" });
export type PlatformOrganizationAccessV1 = z.infer<typeof PlatformOrganizationAccessV1>;

/**
 * Kind of a platform invitation: the first Administrator of an operator (issued from the Platform
 * area to an organization's operator), another Platform Administrator (platform-level), or the
 * recovery of a Platform Administrator whose two-factor authentication was reset (change
 * add-console-account-settings, design D7).
 */
export const PlatformInvitationKind = z.enum([
  "first_administrator",
  "platform_administrator",
  "two_factor_recovery",
]);
export type PlatformInvitationKind = z.infer<typeof PlatformInvitationKind>;

/**
 * A pending platform invitation (not accepted, not expired, not revoked). It NEVER carries the
 * invitation link or token (shown only once, when the invitation is created) nor its digest.
 */
export const PlatformInvitationV1 = z
  .object({
    invitation_id: z.uuid(),
    kind: PlatformInvitationKind,
    email: z.email(),
    /** The operator of a first-Administrator invitation; `null` for a Platform Administrator. */
    operator_id: z.uuid().nullable(),
    organization_id: z.uuid().nullable(),
    issued_by: z.object({ console_user_id: z.uuid(), email: z.email().nullable() }).strict(),
    created_at: IsoDateTime,
    expires_at: IsoDateTime,
  })
  .strict()
  .meta({ id: "PlatformInvitationV1" });
export type PlatformInvitationV1 = z.infer<typeof PlatformInvitationV1>;

/** `GET /platform/invitations?status=pending`: page-based, newest first. */
export const PlatformInvitationListQueryV1 = z
  .object({ status: z.literal("pending"), ...PageParams })
  .strict()
  .meta({ id: "PlatformInvitationListQueryV1" });
export type PlatformInvitationListQueryV1 = z.infer<typeof PlatformInvitationListQueryV1>;

export const PlatformInvitationListResponseV1 = z
  .object({
    total: z.int().nonnegative(),
    page: z.int().min(1),
    size: z.int().min(1),
    items: z.array(PlatformInvitationV1),
  })
  .strict()
  .meta({ id: "PlatformInvitationListResponseV1" });
export type PlatformInvitationListResponseV1 = z.infer<typeof PlatformInvitationListResponseV1>;

/** `POST /platform/invitations/{id}/revoke`: no fields; answers `204` (audited `invitation_revoked`). */
export const RevokePlatformInvitationRequestV1 = z
  .object({})
  .strict()
  .meta({ id: "RevokePlatformInvitationRequestV1" });
export type RevokePlatformInvitationRequestV1 = z.infer<typeof RevokePlatformInvitationRequestV1>;

export const PlatformInvitationParamsV1 = z.object({ id: z.uuid() }).strict();

// ---- Platform Administrators section (change add-console-account-settings, design D10) ---------

/** Status of a Platform Administrator, as for any console user. */
export const PlatformAdministratorStatusV1 = z
  .enum(["active", "locked", "deactivated"])
  .meta({ id: "PlatformAdministratorStatusV1" });
export type PlatformAdministratorStatusV1 = z.infer<typeof PlatformAdministratorStatusV1>;

/**
 * A Platform Administrator in `GET /platform/administrators` (spec platform-administration,
 * "[FR] Platform Administrators management"): email, display name (`null` when not set), whether
 * a TOTP authenticator is set up, status and since when. No organization, no operator access.
 */
export const PlatformAdministratorV1 = z
  .object({
    console_user_id: z.uuid(),
    email: z.email(),
    display_name: z.string().min(1).max(80).nullable(),
    two_factor: z.boolean(),
    status: PlatformAdministratorStatusV1,
    created_at: IsoDateTime,
  })
  .strict()
  .meta({ id: "PlatformAdministratorV1" });
export type PlatformAdministratorV1 = z.infer<typeof PlatformAdministratorV1>;

/** Most Platform Administrators `GET /platform/administrators` answers (no pagination, design D10). */
export const PLATFORM_ADMINISTRATORS_CAP = 200;

/** `GET /platform/administrators`: active ones first, then by email; at most 200. */
export const PlatformAdministratorListResponseV1 = z
  .object({ items: z.array(PlatformAdministratorV1).max(PLATFORM_ADMINISTRATORS_CAP) })
  .strict()
  .meta({ id: "PlatformAdministratorListResponseV1" });
export type PlatformAdministratorListResponseV1 = z.infer<
  typeof PlatformAdministratorListResponseV1
>;

export const PlatformAdministratorParamsV1 = z.object({ id: z.uuid() }).strict();

/**
 * `POST /platform/administrators/{id}/deactivate`: no fields. Terminal; ends the sessions, disables
 * the pool user and removes it from `strust-platform-admin`. Refused with
 * `409 last_platform_administrator` for oneself or the last active Platform Administrator.
 */
export const DeactivatePlatformAdministratorRequestV1 = z
  .object({})
  .strict()
  .meta({ id: "DeactivatePlatformAdministratorRequestV1" });
export type DeactivatePlatformAdministratorRequestV1 = z.infer<
  typeof DeactivatePlatformAdministratorRequestV1
>;

/**
 * `POST /platform/administrators/{id}/two-factor/reset`: no fields (flag `iam.two-factor`). Removes
 * the other Platform Administrator's authenticator and answers `InvitationIssuedResponseV1` with a
 * `two_factor_recovery` invitation (design D7).
 */
export const ResetPlatformAdministratorTwoFactorRequestV1 = z
  .object({})
  .strict()
  .meta({ id: "ResetPlatformAdministratorTwoFactorRequestV1" });
export type ResetPlatformAdministratorTwoFactorRequestV1 = z.infer<
  typeof ResetPlatformAdministratorTwoFactorRequestV1
>;

export const DeactivatedPlatformAdministratorResponseV1 = z
  .object({ console_user_id: z.uuid(), sessions_ended: z.int().min(0) })
  .strict()
  .meta({ id: "DeactivatedPlatformAdministratorResponseV1" });
export type DeactivatedPlatformAdministratorResponseV1 = z.infer<
  typeof DeactivatedPlatformAdministratorResponseV1
>;
